import {
  forwardRef,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ForwardedRef,
  type PropsWithChildren,
} from 'react';
import { createPortal } from 'react-dom';

import { usePageLayoutPortalContext } from './PageLayoutPortalContext.js';
import {
  contentDefaults,
  detailsDefaults,
  sidebarDefaults,
} from './slot-defaults.js';
import { useRegisteredSlot } from './useRegisteredSlot.js';
import type { AriaLabelingProps } from '../../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../core/types/data-props.js';
import type { MaskingProps } from '../../../core/types/masking-props.js';
import type { StylingProps } from '../../../core/types/styling-props.js';
import { DetailsControlBar } from '../details-layout/DetailsControlBar.js';
import {
  DetailsLayoutContext,
  type DetailsLayoutContextValue,
} from '../details-layout/DetailsLayoutContext.js';
import type { DetailsProps, ContentProps } from '../details-layout/slots.js';
import type { SidebarProps } from '../sidebar-layout/slots.js';

const toRef = <T,>(ref: ForwardedRef<T>) => ref ?? undefined;

const noop = () => undefined;

/**
 * Placeholder context used while the real `DetailsLayoutInner` value is still
 * being bridged through the slot registry (it is published one commit late via
 * a layout effect). Keeps `DetailsControlBar` from reading a null context on
 * first render when it is correctly placed inside the portaled details
 * children. Replaced by the live value on the next commit.
 */
const PENDING_DETAILS_CONTEXT: DetailsLayoutContextValue = {
  collapsed: false,
  setCollapsed: noop,
  preferredLayout: 'split',
  setPreferredLayout: noop,
  mode: 'split',
};

/**
 * Returns a stable reference to `value` that only changes identity when one of
 * its own enumerable keys changes (shallow equality). Used to keep registry
 * entries from churning when callers spread anonymous objects (e.g. `domProps`)
 * into them on every render.
 */
function useShallowStable<T extends Record<string, unknown>>(value: T): T {
  const ref = useRef<T>(value);
  const prev = ref.current;
  const prevKeys = Object.keys(prev);
  const nextKeys = Object.keys(value);
  let equal = prevKeys.length === nextKeys.length;
  if (equal) {
    for (const key of nextKeys) {
      if (!Object.is(prev[key], value[key])) {
        equal = false;
        break;
      }
    }
  }
  if (!equal) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * PageLayout.Header - portals children into the header host node and
 * registers layout config via the slot registry in a layout effect.
 * @public
 */
export interface HeaderProps
  extends PropsWithChildren,
    BehaviorTrackingProps,
    AriaLabelingProps,
    StylingProps,
    MaskingProps,
    DataTestId {}

export const PageLayoutHeader = forwardRef<HTMLDivElement, HeaderProps>(
  function PageLayoutHeader(
    { children, ...domProps }: HeaderProps,
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const { hosts } = usePageLayoutPortalContext();
    const stableDomProps = useShallowStable(domProps);
    const refValue = toRef(ref);
    const config = useMemo(
      () => ({ domProps: stableDomProps, ref: refValue }),
      [stableDomProps, refValue],
    );

    useRegisteredSlot('header', config);

    return createPortal(children, hosts.header);
  },
);

/**
 * PageLayout.Sidebar - portals children into the sidebar host node and
 * registers sidebar config via the slot registry in a layout effect.
 * @internal
 */
export const PageLayoutSidebar = forwardRef<HTMLElement, SidebarProps>(
  function PageLayoutSidebar(
    {
      children,
      defaultWidth = sidebarDefaults.defaultWidth,
      minWidth = sidebarDefaults.minWidth,
      maxWidth = sidebarDefaults.maxWidth,
      resizable = sidebarDefaults.resizable,
      collapsed,
      defaultCollapsed = sidebarDefaults.defaultCollapsed,
      onCollapsedChange,
      onBreakpointTransition,
      breakpoint = sidebarDefaults.breakpoint,
      onResize,
      ...domProps
    }: SidebarProps,
    ref: ForwardedRef<HTMLElement>,
  ) {
    const { hosts } = usePageLayoutPortalContext();
    const stableDomProps = useShallowStable(domProps);
    const refValue = toRef(ref);
    const config = useMemo(
      () => ({
        config: {
          defaultWidth,
          minWidth,
          maxWidth,
          resizable,
          collapsed,
          defaultCollapsed,
          onCollapsedChange,
          onBreakpointTransition,
          breakpoint,
          onResize,
        },
        domProps: stableDomProps,
        ref: refValue,
      }),
      [
        defaultWidth,
        minWidth,
        maxWidth,
        resizable,
        collapsed,
        defaultCollapsed,
        onCollapsedChange,
        onBreakpointTransition,
        breakpoint,
        onResize,
        stableDomProps,
        refValue,
      ],
    );

    useRegisteredSlot('sidebar', config);

    return createPortal(children, hosts.sidebar);
  },
);

/**
 * PageLayout.Content - portals children into the content host node and
 * registers content config via the slot registry in a layout effect.
 * @internal
 */
export const PageLayoutContent = forwardRef<HTMLElement, ContentProps>(
  function PageLayoutContent(
    {
      children,
      minWidth = contentDefaults.minWidth,
      ...domProps
    }: ContentProps,
    ref: ForwardedRef<HTMLElement>,
  ) {
    const { hosts } = usePageLayoutPortalContext();
    const stableDomProps = useShallowStable(domProps);
    const refValue = toRef(ref);
    const config = useMemo(
      () => ({
        config: { minWidth },
        domProps: stableDomProps,
        ref: refValue,
      }),
      [minWidth, stableDomProps, refValue],
    );

    useRegisteredSlot('content', config);

    return createPortal(children, hosts.content);
  },
);

/**
 * PageLayout.Details - portals children into the details host node and
 * registers details config via the slot registry in a layout effect.
 *
 * Children are wrapped in `DetailsLayoutContext.Provider` using the value
 * bridged through the slot registry. Without this, `DetailsControlBar` would
 * fall back to the context's default value because the portal originates
 * outside the provider placed by `DetailsLayoutInner`.
 *
 * @internal
 */
export const PageLayoutDetails = forwardRef<HTMLElement, DetailsProps>(
  function PageLayoutDetails(
    {
      children,
      minWidth = detailsDefaults.minWidth,
      maxWidth,
      defaultWidth = detailsDefaults.defaultWidth,
      resizable = detailsDefaults.resizable,
      collapsed,
      defaultCollapsed = detailsDefaults.defaultCollapsed,
      onCollapsedChange,
      onBreakpointTransition,
      layout,
      defaultLayout = detailsDefaults.defaultLayout,
      onLayoutChange,
      breakpoint = detailsDefaults.breakpoint,
      onResize,
      width,
      ...domProps
    }: DetailsProps,
    ref: ForwardedRef<HTMLElement>,
  ) {
    const { hosts, registry } = usePageLayoutPortalContext();
    const stableDomProps = useShallowStable(domProps);
    const refValue = toRef(ref);
    const config = useMemo(
      () => ({
        config: {
          minWidth,
          maxWidth,
          defaultWidth,
          resizable,
          collapsed,
          defaultCollapsed,
          onCollapsedChange,
          onBreakpointTransition,
          layout,
          defaultLayout,
          onLayoutChange,
          breakpoint,
          onResize,
          width,
        },
        domProps: stableDomProps,
        ref: refValue,
      }),
      [
        minWidth,
        maxWidth,
        defaultWidth,
        resizable,
        collapsed,
        defaultCollapsed,
        onCollapsedChange,
        onBreakpointTransition,
        layout,
        defaultLayout,
        onLayoutChange,
        breakpoint,
        onResize,
        width,
        stableDomProps,
        refValue,
      ],
    );

    useRegisteredSlot('details', config);

    const detailsLayoutContext = useSyncExternalStore(
      registry.subscribe,
      () => registry.getSnapshot().detailsLayoutContext,
    );

    // The bridged value is published one commit late (DetailsLayoutInner
    // registers it in a layout effect). Until it arrives, provide a non-null
    // pending value so the portaled ControlBar can read the context on first
    // render instead of falling back to the provider default.
    const providedContext = detailsLayoutContext ?? PENDING_DETAILS_CONTEXT;

    return createPortal(
      <DetailsLayoutContext.Provider value={providedContext}>
        {children}
      </DetailsLayoutContext.Provider>,
      hosts.details,
    );
  },
);

export const PageLayoutDetailsWithControlBar = Object.assign(
  PageLayoutDetails,
  { ControlBar: DetailsControlBar },
);
